import React, { ReactElement, useEffect, useState } from 'react'

import { PagedListRequest } from 'domain/types/global/pagedListRequest';

import { useTranslation } from 'react-i18next';
import { CampanhaPedidoResgateResponse } from 'domain/types/_carteira/campanhaPedidoResgate/campanhaPedidoResgateResponse';
import { RootStateCampanha } from '../../../../store/campanhaReducer';
import { GetSolicitadosAprovadosByCampanha, ResetSolicitadosAprovadosByCampanha } from 'domain/store/_carteira/campanhaPedidoResgate/actions/campanhaPedidoResgateAction';
import moment from 'moment';
import 'moment/dist/locale/pt-br'

import { connectUtil, PropsFromRedux } from 'base/src/utils/reduxUtil';
import { ObjectControl } from 'domain/utils/ReactUtils';
import { CAMPANHA_PEDIDORESGATE_APROVADOS_GET_BY_CAMPANHA } from 'domain/types/_carteira/campanhaPedidoResgate/campanhaPedidoResgate';
import { CreateLote } from '../../../../store/pedidoLotes/actions/pedidoLotesAction';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Checkbox,
  Chip,
  SelectChangeEvent,
  Badge
} from '@mui/material';
import { Icon } from '@iconify/react';
import { formatToBRL } from 'base/src/utils/mathUtil';
import { Tag } from 'base/src/components/Tag';
import { Button } from 'base/src/components/Button';
import NoData from 'base/src/components/NoData';
import FilterDrawer from './components/filterDrawer/filterDrawer';

const connector = connectUtil(
  (state: RootStateCampanha) => ({
    CampanhaPedidoResgate: state.CampanhaPedidoResgateAprovadosReducer.CampanhaPedidoResgateAprovadosByCampanha,
    CampanhasDisponiveis: state.CampanhasReducer?.campanhasAll,
    mudancaStatus: state.CampanhaPedidoResgateReducer.mudancaStatus,
    loading: state.BaseReducer.Loading
  }),
  { GetSolicitadosAprovadosByCampanha, ResetSolicitadosAprovadosByCampanha, CreateLote }
);

export interface PagedResgatesProps extends PropsFromRedux<typeof connector> {
  take: number;
  campanhaId: number;
  action?: React.ReactElement;
  control: ObjectControl<CampanhaPedidoResgateResponse>;
  navigatePagarPedidoButton?: ReactElement;
}

function PagedGerarLote(props: PagedResgatesProps) {
  const [request, setRequest] = useState<PagedListRequest<CampanhaPedidoResgateResponse>>({
    skip: 0,
    take: props.take,
    search: '',
    filter: [],
    data: {} as CampanhaPedidoResgateResponse
  });

  const [searchValue, setSearchValue] = useState('');
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [selectedItemsData, setSelectedItemsData] = useState<Map<number, CampanhaPedidoResgateResponse>>(new Map());
  const [premiacaoTipoFilter, setPremiacaoTipoFilter] = useState<string[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');

  // Debug controlado para identificar renders infinitos
  useEffect(() => {
    console.log('PagedGerarLote renderizou - setRequest:', setRequest);
  });

  const { t } = useTranslation();

  moment.locale("pt-br");

  // Premiacao tipo options
  const premiacaoTipoOptions = [
    { id: '1', nome: 'Por Ranking' },
    { id: '4', nome: 'WebGold' }
  ];

  // Handle pagination change
  function handlePageChange(_: unknown, newPage: number) {
    const newRequest = {
      ...request,
      skip: newPage * request.take
    };
    setRequest(newRequest);

    // Reset data to clear previous page data
    props.ResetSolicitadosAprovadosByCampanha(newRequest, props.take);

    // Load new page data
    props.GetSolicitadosAprovadosByCampanha([props.campanhaId], newRequest, t);
  }

  // Handle rows per page change
  function handleRowsPerPageChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newTake = parseInt(event.target.value, 10);
    const newRequest = {
      ...request,
      take: newTake,
      skip: 0
    };
    setRequest(newRequest);

    // Reset data to clear previous data
    props.ResetSolicitadosAprovadosByCampanha(newRequest, newTake);

    // Load new data
    props.GetSolicitadosAprovadosByCampanha([props.campanhaId], newRequest, t);
  }

  // Handle search
  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    const searchVal = event.target.value;
    setSearchValue(searchVal);

    const newRequest = {
      ...request,
      search: searchVal,
      skip: 0
    };
    setRequest(newRequest);

    // Reset data to clear previous results
    props.ResetSolicitadosAprovadosByCampanha(newRequest, props.take);

    // Load new filtered data
    props.GetSolicitadosAprovadosByCampanha([props.campanhaId], newRequest, t);
  }

  function handlePremiacaoTipoFilterChange(event: SelectChangeEvent<string[]>) {
    const values = event.target.value as string[];
    setPremiacaoTipoFilter(values);

    const filterArray = values.length > 0 ? [{
      Values: values,
      Id: 0,
      Name: "PremiacaoTipo"
    }] : [];

    const newRequest = {
      ...request,
      filter: filterArray,
      skip: 0
    };
    setRequest(newRequest);

    props.ResetSolicitadosAprovadosByCampanha(newRequest, props.take);

    props.GetSolicitadosAprovadosByCampanha([props.campanhaId], newRequest, t);
  }

  // Handle premiacao tipo chip toggle
  function handlePremiacaoTipoChipToggle(tipoId: string) {
    const newFilter = premiacaoTipoFilter.includes(tipoId)
      ? premiacaoTipoFilter.filter(id => id !== tipoId)
      : [...premiacaoTipoFilter, tipoId];

    setPremiacaoTipoFilter(newFilter);

    const filterArray = newFilter.length > 0 ? [{
      Values: newFilter,
      Id: 0,
      Name: "PremiacaoTipo"
    }] : [];

    const newRequest = {
      ...request,
      filter: filterArray,
      skip: 0
    };
    setRequest(newRequest);

    props.ResetSolicitadosAprovadosByCampanha(newRequest, props.take);
    props.GetSolicitadosAprovadosByCampanha([props.campanhaId], newRequest, t);
  }

  function handleOpenDrawer() {
    setIsDrawerOpen(true);
  }

  function handleCloseDrawer() {
    setIsDrawerOpen(false);
  }

  function handleApplyFilters() {
    setIsDrawerOpen(false);
  }

  function handleDateStartChange(value: string) {
    setDateStart(value);

    const newRequest = {
      ...request,
      data: {
        ...request.data,
        DataInicio: value ? `${value}T00:00:00` : undefined,
        DataFim: dateEnd ? `${dateEnd}T23:59:59` : undefined
      },
      skip: 0
    };
    setRequest(newRequest);

    props.ResetSolicitadosAprovadosByCampanha(newRequest, props.take);
    props.GetSolicitadosAprovadosByCampanha([props.campanhaId], newRequest, t);
  }

  function handleDateEndChange(value: string) {
    setDateEnd(value);

    const newRequest = {
      ...request,
      data: {
        ...request.data,
        DataInicio: dateStart ? `${dateStart}T00:00:00` : undefined,
        DataFim: value ? `${value}T23:59:59` : undefined
      },
      skip: 0
    };
    setRequest(newRequest);

    props.ResetSolicitadosAprovadosByCampanha(newRequest, props.take);
    props.GetSolicitadosAprovadosByCampanha([props.campanhaId], newRequest, t);
  }

  function handleClearFilters() {
    setSearchValue('');
    setPremiacaoTipoFilter([]);
    setDateStart('');
    setDateEnd('');

    // Reset request to initial state
    const newRequest = {
      ...request,
      search: '',
      filter: [],
      data: {} as CampanhaPedidoResgateResponse,
      skip: 0
    };
    setRequest(newRequest);

    // Reset data and load fresh data without filters
    props.ResetSolicitadosAprovadosByCampanha(newRequest, props.take);
    props.GetSolicitadosAprovadosByCampanha([props.campanhaId], newRequest, t);
  }

  // Handle select all
  function handleSelectAll() {
    const currentPageIds = data.map(item => item.CampanhaPedidoResgateID);
    const allCurrentSelected = currentPageIds.every(id => selectedItems.includes(id));

    if (allCurrentSelected) {
      // Deselect all items from current page
      setSelectedItems(prev => prev.filter(id => !currentPageIds.includes(id)));
      setSelectedItemsData(prev => {
        const newMap = new Map(prev);
        currentPageIds.forEach(id => newMap.delete(id));
        return newMap;
      });
    } else {
      // Select all items from current page
      setSelectedItems(prev => {
        const newIds = [...prev];
        currentPageIds.forEach(id => {
          if (!newIds.includes(id)) {
            newIds.push(id);
          }
        });
        return newIds;
      });
      setSelectedItemsData(prev => {
        const newMap = new Map(prev);
        data.forEach(item => {
          newMap.set(item.CampanhaPedidoResgateID, item);
        });
        return newMap;
      });
    }
  }

  // Handle individual item selection
  function handleSelectItem(itemId: number, itemData: CampanhaPedidoResgateResponse) {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });

    setSelectedItemsData(prev => {
      const newMap = new Map(prev);
      if (newMap.has(itemId)) {
        newMap.delete(itemId);
      } else {
        newMap.set(itemId, itemData);
      }
      return newMap;
    });
  }

  // Format pagination label
  function formatPaginationLabel({ from, to, count }: { from: number; to: number; count: number }) {
    return `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`;
  }

  // Map column keys to backend sort field paths
  const sortFieldMap: Record<string, string> = {
    'usuario': '_Usuario.NomeCompleto',
    'premiacao': 'Premiacao.Titulo',
    'tipo': 'Premiacao.PremiacaoTipo.PremiacaoTipoID',
    'data': 'HoraPedido',
    'status': 'CampanhaPedidoResgateStatus.Titulo',
    'valor': 'ValorTotal'
  };

  // Handle column sort (server-side)
  function handleSort(field: string) {
    const backendField = sortFieldMap[field];
    if (!backendField) return;

    const isAsc = sortField === field && sortOrder === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setSortOrder(newOrder);
    setSortField(field);

    const newRequest = {
      ...request,
      sort: `${backendField} ${newOrder}`,
      skip: 0
    };
    setRequest(newRequest);

    props.ResetSolicitadosAprovadosByCampanha(newRequest, props.take);
    props.GetSolicitadosAprovadosByCampanha([props.campanhaId], newRequest, t);
  }

  // Render sort icon
  function renderSortIcon(field: string) {
    if (sortField !== field) {
      return <Icon icon="mdi:unfold-more-horizontal" style={{ marginLeft: 4, fontSize: 16, opacity: 0.5 }} />;
    }
    return sortOrder === 'asc'
      ? <Icon icon="mdi:arrow-up" style={{ marginLeft: 4, fontSize: 16 }} />
      : <Icon icon="mdi:arrow-down" style={{ marginLeft: 4, fontSize: 16 }} />;
  }

  useEffect(() => {
    props.ResetSolicitadosAprovadosByCampanha(request, props.take);
  }, [props.mudancaStatus]);

  // Load initial data
  useEffect(() => {
    props.GetSolicitadosAprovadosByCampanha([props.campanhaId], request, t);
  }, [props.campanhaId]);

  // Get data from state
  const data = props.CampanhaPedidoResgate?.Result || [];
  const totalCount = props.CampanhaPedidoResgate?.TotalCount || 0;
  const isLoading = props.loading.some(l => l.Name === CAMPANHA_PEDIDORESGATE_APROVADOS_GET_BY_CAMPANHA);

  const currentPage = Math.floor(request.skip / request.take);
  const activeFilterCount = premiacaoTipoFilter.length + (dateStart ? 1 : 0) + (dateEnd ? 1 : 0);

  // Sincronizar itens selecionados com o control
  useEffect(() => {
    // Usar todos os itens selecionados armazenados no Map
    const selectedData = Array.from(selectedItemsData.values());

    // Atualizar o control com os itens selecionados
    if (props.control) {
      props.control.setObject('Premiacao', selectedData);
    }
  }, [selectedItemsData, props.control]);

  return (
    <Box sx={{
      '& @keyframes fadeIn': {
        '0%': {
          opacity: 0,
          transform: 'translateY(10px)'
        },
        '100%': {
          opacity: 1,
          transform: 'translateY(0)'
        }
      }
    }}>
      {/* Action Bar: Botão Voltar - SearchBar - Filtros - Ações */}
      <Box sx={{
        mb: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap'
      }}>
        {/* Left Side: Botão Voltar + Search Bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          {/* Botão Voltar */}
          {props.navigatePagarPedidoButton && (
            <Box sx={{
              '& button': {
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '& .MuiButton-startIcon': {
                  marginRight: 0.5
                }
              }
            }}>
              {React.cloneElement(props.navigatePagarPedidoButton as React.ReactElement, {
                startIcon: <Icon icon="mdi:arrow-left" />
              })}
            </Box>
          )}

          {/* Search Bar */}
          <TextField
            label="Pesquisar pedidos"
            placeholder="Digite o nome do usuário"
            value={searchValue}
            onChange={handleSearchChange}
            variant="outlined"
            size="small"
            sx={{
              minWidth: 300,
              flex: 1,
              maxWidth: 400,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              }
            }}
            InputProps={{
              startAdornment: (
                <Icon
                  icon="mdi:magnify"
                  style={{
                    marginRight: 8,
                    color: '#9CA3AF',
                    width: 20,
                    height: 20
                  }}
                />
              ),
            }}
          />
        </Box>

        {/* Right Side: Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Badge
            badgeContent={activeFilterCount}
            color="primary"
            invisible={activeFilterCount === 0}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.7rem',
                height: 20,
                minWidth: 20,
                borderRadius: '50%',
              }
            }}
          >
            <Button
              size="md"
              variant="secondary"
              iconName="mdi:filter-variant"
              onClick={handleOpenDrawer}
            >
              Filtros
            </Button>
          </Badge>
        </Box>
      </Box>

      {/* Premiacao Tipo Tags */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {premiacaoTipoOptions.map(function renderPremiacaoChip(tipo) {
          const isSelected = premiacaoTipoFilter.includes(tipo.id);

          return (
            <Chip
              key={tipo.id}
              label={tipo.nome}
              onClick={() => handlePremiacaoTipoChipToggle(tipo.id)}
              variant={isSelected ? 'filled' : 'outlined'}
              color="primary"
              size="small"
              sx={{
                fontWeight: isSelected ? 600 : 400,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }
              }}
            />
          );
        })}
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedItems.length > 0 && selectedItems.length < totalCount}
                  checked={data.length > 0 && data.every(item => selectedItems.includes(item.CampanhaPedidoResgateID))}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('usuario')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Usuário
                  {renderSortIcon('usuario')}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'grey.700'
                }}
              >
                Contato
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('premiacao')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Premiação
                  {renderSortIcon('premiacao')}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('tipo')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Tipo de Premiação
                  {renderSortIcon('tipo')}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('data')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Data Solicitação
                  {renderSortIcon('data')}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('status')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Status
                  {renderSortIcon('status')}
                </Box>
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 600,
                  color: 'grey.700',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => handleSort('valor')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  Valor
                  {renderSortIcon('valor')}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 8, border: 'none' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={40} thickness={4} />
                    <Typography variant="body2" color="text.secondary">
                      Carregando dados...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, border: 'none' }}>
                  <NoData />
                </TableCell>
              </TableRow>
            ) : (
              data.map((item, index) => {
                const isSelected = selectedItems.includes(item.CampanhaPedidoResgateID);

                function handleItemSelection() {
                  handleSelectItem(item.CampanhaPedidoResgateID, item);
                }

                return (
                  <TableRow
                    key={item.CampanhaPedidoResgateID || index}
                    hover
                    selected={isSelected}
                    sx={{
                      '&:hover': {
                        bgcolor: 'action.hover',
                        cursor: 'pointer',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                      },
                      '&:nth-of-type(even)': {
                        bgcolor: 'grey.25'
                      },
                      transition: 'all 0.2s ease-in-out',
                      animation: 'fadeIn 0.3s ease-in-out'
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={handleItemSelection}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ color: 'grey.900' }}>
                        {item._Usuario?.NomeCompleto}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box>
                        {item._Usuario?.Email && (
                          <Typography variant="body2" sx={{ color: 'grey.700' }}>
                            {item._Usuario.Email}
                          </Typography>
                        )}
                        {item._Usuario?.TelefoneCelular && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'grey.600',
                              fontSize: '0.75rem',
                              display: 'block'
                            }}
                          >
                            {item._Usuario.TelefoneCelular}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'grey.700' }}>
                        {item.Premiacao?.Titulo}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'grey.700' }}>
                        {item.Premiacao?.PremiacaoTipo?.Titulo}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'grey.700' }}>
                        {moment(item.HoraPedido).format('DD/MM/YYYY HH:mm')}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {item.CampanhaPedidoResgateStatusID && (
                        <Tag
                          variant={
                            item.CampanhaPedidoResgateStatusID === 1
                              ? 'warning'
                              : item.CampanhaPedidoResgateStatusID === 2
                                ? 'positive'
                                : item.CampanhaPedidoResgateStatusID === 3
                                  ? 'danger'
                                  : 'neutral'
                          }
                        >
                          {item.CampanhaPedidoResgateStatusID === 1
                            ? 'Pendente'
                            : item.CampanhaPedidoResgateStatusID === 2
                              ? 'Aprovado'
                              : item.CampanhaPedidoResgateStatusID === 3
                                ? 'Rejeitado'
                                : item.CampanhaPedidoResgateStatusID}
                        </Tag>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'success.main',
                            fontWeight: 700,
                            fontSize: '0.95rem'
                          }}
                        >
                          {formatToBRL(item.ValorOriginal || '0')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'grey.600' }}>
                          Valor original
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box sx={{ backgroundColor: 'grey.25', borderTop: '1px solid', borderColor: 'grey.200' }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={currentPage}
            onPageChange={handlePageChange}
            rowsPerPage={request.take}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={formatPaginationLabel}
            sx={{
              '& .MuiTablePagination-toolbar': {
                paddingX: 3,
                paddingY: 2
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                color: 'grey.700',
                fontWeight: 500
              },
              '& .MuiTablePagination-actions button': {
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.50'
                }
              }
            }}
          />
        </Box>
      </TableContainer>

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        premiacaoTipoFilter={premiacaoTipoFilter}
        onPremiacaoTipoFilterChange={handlePremiacaoTipoFilterChange}
        dateStart={dateStart}
        dateEnd={dateEnd}
        onDateStartChange={handleDateStartChange}
        onDateEndChange={handleDateEndChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        premiacaoTipoOptions={premiacaoTipoOptions}
      />
    </Box>
  );
}
export default connector(PagedGerarLote);